<template>
  <div class="proposal-basket" data-test-component="proposalBasket">
    <page-spinner :spinner-url-list="spinnerUrlList" />
    <slot name="topmobile" />
    <two-columns-page-layout v-if="isViewModelLoaded" v-cloak>
      <template #left>
        <proposal-list
          data-test-proposal-basket-list
          :title="viewModel.basketTitle"
          :is-loading="!isViewModelLoaded"
          :add-proposal-button-label="viewModel.addProposalButtonLabel"
          :covers-label="viewModel.coversLabel"
          :per-year-label="viewModel.perYearLabel"
          :clauses-label="viewModel.clausesLabel"
          :deductible-label="viewModel.deductibleLabel"
          :no-items-label="viewModel.noItemsLabel"
          :quarterly-payment-label="viewModel.quarterlyPaymentLabel"
          :monthly-payment-label="viewModel.monthlyPaymentLabel"
          :half-yearly-payment-label="viewModel.halfYearlyPaymentLabel"
          :remove-modal-headline="viewModel.removeModalHeadline"
          :remove-modal-yes-label="viewModel.removeModalYesLabel"
          :remove-modal-no-label="viewModel.removeModalNoLabel"
          :download-proposal-label="viewModel.downloadProposalLabel"
          :remove-proposal-label="viewModel.removeProposalLabel"
          @add-proposal="showAddProposalModal"
        />
      </template>

      <template #right>
        <basket-price-list
          data-test-basket-price-list
          :headline="viewModel.basketPriceListTitle"
          :total-price-label="viewModel.totalPriceLabel"
          :total-fees-label="viewModel.totalFeesLabel"
          :per-year-label="viewModel.perYearLabel"
          :proposals="selectedProposals"
          :total-price="totalPrice"
          :total-fees="totalFees"
          :digital-bonus-label="viewModel.digitalBonusLabel"
          :digital-bonus-tooltip="viewModel.digitalBonusTooltip"
          :year-zero-text="viewModel.childYearZeroText"
          :year-zero-modal="viewModel.childYearZeroModal"
        >
          <template #actions>
            <div class="proposal-basket__actions">
              <button
                data-test-proposal-basket-continue-button
                class="button large"
                :disabled="noProposalSelected"
                @click="continueButtonClick"
                v-html="viewModel.continueButtonLabel"
              />
            </div>
          </template>
        </basket-price-list>

        <!-- Sitecore Placeholder "phRightDesktopTopMobile" -->
        <div class="proposal-basket__placeholder-desktop">
          <slot name="rightcolumn" />
        </div>
      </template>
    </two-columns-page-layout>

    <remove-proposal-message-modal
      data-test-remove-proposal-error-modal
      :open="showRemoveProposalErrorModal"
      :headline="viewModel.removeErrorModalHeadline"
      :content="viewModel.removeErrorModalText"
      :button-label="viewModel.removeErrorModalButtonLabel"
      @close="dismissRemoveErrorModal"
    />
    <remove-proposal-message-modal
      data-test-remove-proposal-success-modal
      :open="showRemoveProposalSuccessModal"
      :headline="viewModel.removeSuccessModalHeadline"
      :content="viewModel.removeSuccessModalText"
      :button-label="viewModel.removeSuccessModalButtonLabel"
      @close="dismissRemoveSuccessModal"
    />
    <add-proposal-modal
      :open="addProposalModalOpen"
      :datasource="viewModel.addProposalOverlayItemId"
      @close="hideAddProposalModal"
    />
  </div>
</template>
<script lang="ts">
import ProposalList from './components/ProposalList.vue';
import { BasketContentModelDto, ProposalBasketData, BasketViewModel } from './types/ProposalBasket';
import BasketPriceList from '@/components/Feature/Basket/BasketPriceList.vue';
import AddProposalModal from '@/components/Feature/Basket/AddProposalModal.vue';
import TwoColumnsPageLayout from '@/components/UI/TwoColumnsPageLayout.vue';
import RequestService from '@/common/requestService';
import Utility from '@/common/UtilityFunctions.js';

import RemoveProposalMessageModal from '@/components/Feature/Basket/RemoveProposalMessageModal.vue';
import PageSpinner from '@/components/UI/PageSpinner.vue';
import { formatPrice } from '@/components/Feature/Basket/utilities/basketUtilities';
import {
  BasketProposal,
  ProposalBasketStoreState
} from '@/store/modules/interfaces/ProposalBasketStoreState';
import { mapState } from 'vuex';
import { defineComponent, PropType } from 'vue';

export default defineComponent({
  name: 'ProposalBasket',
  components: {
    BasketPriceList,
    ProposalList,
    AddProposalModal,
    TwoColumnsPageLayout,
    RemoveProposalMessageModal,
    PageSpinner
  },
  props: {
    datasource: {
      type: [String, null] as PropType<string | null>,
      default() {
        return null;
      }
    }
  },
  data(): ProposalBasketData {
    return {
      viewModel: {
        basketTitle: '',
        basketPriceListTitle: '',
        addProposalButtonLabel: '',
        continueButtonLabel: '',
        continueButtonLink: '',
        totalPriceLabel: '',
        totalFeesLabel: '',
        perYearLabel: '',
        coversLabel: '',
        addProposalOverlayItemId: '',
        deductibleLabel: '',
        noItemsLabel: '',
        removeModalHeadline: '',
        removeModalYesLabel: '',
        removeModalNoLabel: '',
        removeErrorModalHeadline: '',
        removeErrorModalText: '',
        removeErrorModalButtonLabel: '',
        removeSuccessModalHeadline: '',
        removeSuccessModalText: '',
        removeSuccessModalButtonLabel: '',
        digitalBonusLabel: '',
        digitalBonusTooltip: '',
        clausesLabel: '',
        halfYearlyPaymentLabel: '',
        monthlyPaymentLabel: '',
        quarterlyPaymentLabel: '',
        removeProposalLabel: '',
        downloadProposalLabel: '',
        childYearZeroText: '',
        childYearZeroModal: {
          textLink: '',
          title: '',
          textContent: '',
          closeBtn: ''
        }
      },
      addProposalModalOpen: false,
      isViewModelLoaded: false
    };
  },

  computed: {
    ...mapState('proposalBasket', {
      proposals: (state) => (state as ProposalBasketStoreState).proposals,
      selectedProposalIds: (state) => (state as ProposalBasketStoreState).selectedProposals,
      showRemoveProposalErrorModal: (state) =>
        (state as ProposalBasketStoreState).showRemoveProposalErrorModal,
      showRemoveProposalSuccessModal: (state) =>
        (state as ProposalBasketStoreState).showRemoveProposalSuccessModal
    }),
    spinnerUrlList(): string[] {
      let spinnerUrlArray = [];
      spinnerUrlArray.push(`${Utility.constants().API.basket_get}/${this.datasource}`);
      spinnerUrlArray.push(Utility.constants().API.proposal_delete);
      spinnerUrlArray.push(Utility.constants().API.proposal_get_all);
      spinnerUrlArray.push(Utility.constants().API.proposal_update_paymentfrequency);

      this.proposals.forEach((item) => {
        const extProposalId = item.externalProposalNumber.replace(/\//g, '-');
        item.paymentInformation.paymentIntervals.forEach((interval) => {
          spinnerUrlArray.push(
            `${
              Utility.constants().API.proposal_update_paymentfrequency
            }${extProposalId}/${interval.id.toString()}`
          );
        });
      });

      return spinnerUrlArray;
    },
    selectedProposals(): BasketProposal[] {
      return this.proposals.filter((proposal) => this.selectedProposalIds.includes(proposal.id));
    },
    totalPrice(): string {
      let totalPrice = 0;
      if (this.selectedProposals.length === 0) return totalPrice.toString();
      this.selectedProposals.forEach((proposal) => (totalPrice += proposal.price));
      return formatPrice(totalPrice);
    },
    totalFees(): string {
      let totalFees = 0;
      if (this.selectedProposals.length === 0) return totalFees.toString();
      this.selectedProposals.forEach((proposal) => (totalFees += proposal.fees));
      return formatPrice(totalFees);
    },
    noProposalSelected(): boolean {
      return !this.selectedProposals || this.selectedProposals.length === 0;
    }
  },
  beforeCreate() {
    this.$store.dispatch('proposalBasket/initializeBasket');
  },
  mounted() {
    this.getViewModel();
  },
  methods: {
    showAddProposalModal() {
      this.addProposalModalOpen = true;
      this.$store.dispatch('Events/register', {
        event: 'proposal-basket-add-insurance',
        id: 'proposalBasket',
        vm: this.proposals
      });
    },
    hideAddProposalModal() {
      this.addProposalModalOpen = false;
    },
    mapFromBasketViewModelDto(content: BasketContentModelDto): BasketViewModel {
      return {
        basketTitle: content.title,
        basketPriceListTitle: content.labelPrice,
        addProposalButtonLabel: content.overlayButtonText,
        continueButtonLabel: content.labelButtonContinue,
        totalPriceLabel: content.labelTotalPrice,
        totalFeesLabel: content.labelTotalFees,
        perYearLabel: content.labelPrYear,
        coversLabel: content.labelSelectedCovers,
        addProposalOverlayItemId: content.overlayItemId,
        continueButtonLink: content.linkButtonContinue,
        deductibleLabel: content.labelDeductible,
        noItemsLabel: content.labelNoItems,
        removeModalHeadline: content.headlineModalRemove,
        removeModalYesLabel: content.labelModalRemoveYes,
        removeModalNoLabel: content.labelModalRemoveNo,
        removeErrorModalHeadline: content.headlineModalRemoveError,
        removeErrorModalText: content.textModalRemoveError,
        removeErrorModalButtonLabel: content.labelButtonModalRemoveError,
        removeSuccessModalHeadline: content.headlineModalRemoveSuccess,
        removeSuccessModalText: content.textModalRemoveSuccess,
        removeSuccessModalButtonLabel: content.labelButtonModalRemoveSuccess,
        digitalBonusLabel: content.digitalBonusLabel,
        digitalBonusTooltip: content.digitalBonusTooltip,
        clausesLabel: content.labelClauses,
        halfYearlyPaymentLabel: content.labelHalfYearlyPayment,
        monthlyPaymentLabel: content.labelMonthlyPayment,
        quarterlyPaymentLabel: content.labelQuarterlyPayment,
        removeProposalLabel: content.labelRemoveProposal,
        downloadProposalLabel: content.labelDownloadProposal,
        childYearZeroText: content.textChildAccidentYearZero,
        childYearZeroModal: {
          textLink: content.modalTextLink,
          title: content.modalTitle,
          textContent: content.modalTextContent,
          closeBtn: content.modalCloseBtn
        }
      };
    },
    async getViewModel() {
      if (this.datasource === null) return;

      await RequestService.get<BasketContentModelDto>(
        `${Utility.constants().API.basket_get}/${this.datasource}`
      )
        .then((response) => {
          const viewModel = this.mapFromBasketViewModelDto(response.data);
          this.viewModel = viewModel;
          this.isViewModelLoaded = true;
        })
        .catch((err) => Utility.logError(err, this));
    },
    dismissRemoveSuccessModal() {
      this.$store.dispatch('proposalBasket/dismissRemoveSuccess');
    },
    dismissRemoveErrorModal() {
      this.$store.dispatch('proposalBasket/dismissRemoveError');
    },
    continueButtonClick() {
      this.$store.dispatch('Events/register', {
        event: 'proposal-basket-continue',
        id: 'proposalBasket',
        vm: this.selectedProposals
      });

      location.href = this.viewModel.continueButtonLink;
    }
  }
});
</script>
<style lang="scss" scoped>
@import 'src/styles/all.scss';

.proposal-basket {
  background-color: white;

  &__placeholder-desktop {
    margin-top: rem-calc(48);

    @include breakpoint(medium down) {
      display: none;
    }
  }

  &__actions {
    display: flex;

    > .button:not(:first-child) {
      margin-right: rem-calc(10);
    }

    @include breakpoint(medium down) {
      flex-direction: row;
    }

    @include breakpoint(large up) {
      flex-direction: column-reverse;
    }
  }
}
</style>
../../../../common/requestService
